import { type ApolloError, useQuery } from '@apollo/react-hooks';
import gql from 'graphql-tag';
import type { SlotApplicationsInfo, StudentCardCollectionInfo } from './types';

const GET_ALL_SLOTS_APPLICATION = gql`
  query {
    allSlotsApplications {
      date
      slots {
        slotId
        slotName
        meridiem
        quotaLimit
        applications {
          applicationId
          applicationNo
          studentNumber
          studentName {
            surname
            givenName
          }
        }
      }
    }
  }
`;

type AllSlotsApplications = {
  date: string;
  slots: SlotApplicationsInfo[];
};

type GetStudentCardCollection = {
  allSlotsApplications: AllSlotsApplications[];
  loading: boolean;
  error: ApolloError | undefined;
};

export const useGetAllSlotsApplications = (): GetStudentCardCollection => {
  const { data, loading, error } = useQuery(GET_ALL_SLOTS_APPLICATION, {
    fetchPolicy: 'network-only',
  });

  const fillApplicationsToQuota = (
    applications: StudentCardCollectionInfo[],
    quotaLimit: number,
  ): StudentCardCollectionInfo[] => {
    const placeholdersNeeded = quotaLimit - applications.length;

    if (placeholdersNeeded > 0) {
      const placeholders: StudentCardCollectionInfo[] = Array.from(
        { length: placeholdersNeeded },
        () => ({
          index: '-',
          applicationNo: '-',
          studentNumber: '-',
          applicationId: '-',
          studentName: '-',
        }),
      );

      return [...applications, ...placeholders];
    }

    return applications;
  };

  const allSlotsApplications = (data?.allSlotsApplications ?? []).map(
    (slotApplications: any) => {
      const slots = slotApplications.slots.map((slot: any) => {
        const applications = slot.applications.map(
          (application: any, index: number) => ({
            index: index + 1,
            applicationNo: application.applicationNo,
            studentNumber: application.studentNumber,
            applicationId: application.applicationId,
            studentName: `${application.studentName.surname} ${application.studentName.givenName}`,
          }),
        );

        return {
          slotId: slot.slotId,
          slotName: slot.slotName,
          meridiem: slot.meridiem,
          quotaLimit: slot.quotaLimit,
          applications: fillApplicationsToQuota(applications, slot.quotaLimit),
        };
      });

      return {
        date: slotApplications.date,
        slots,
      };
    },
  );

  return { allSlotsApplications, loading, error };
};
